// react
import React, { useEffect, useState } from 'react'
import ReactTooltip from 'react-tooltip'
import { useParams } from 'react-router-dom'

import { useUser } from '_hooks'

// components
import { Header, Input, Button, Icon, BUTTON_THEME, Select } from '@st-operations/uikit/src/v2'
import toast from '@st-operations/uikit/lib/toast'
import { Dialog3 as Dialog } from '@st-operations/uikit/src/v1/index.js'
import Table from '_components/table/'
// services
import { getMappingIds } from '_services/destinations'
import {
  updateSourcePartner,
  getSourcePartnersById,
  getS3AccessKeys,
  createS3AccessKeys,
  deleteS3AccessKeys,
  deleteApiKeys,
} from '_services/source-partners'
import NewApiKeyModal from '../NewApiKeyModal'
import NewS3AccessKeyModal from '../NewS3AccessKeyModal'
import copyToClipBoard from '../../../../lib/copyToClipBoard'

import { usePDPEnvironment } from '../../../../contexts/PDPEnvContext'

import './styles.scss'

const DeleteButton = ({ onConfirm, type }) => {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <div>
      <button className="source-partner__row-delete" onClick={() => setIsOpen(true)}>
        <Icon icon="trash" type="outline" color="#c61e1eff" height="20px" width="20px" />
      </button>
      <Dialog open={isOpen} onClose={setIsOpen} close={() => setIsOpen(false)}>
        <section className="new-api-key-modal">
          <div className="source-partner__delete-confirm-modal">
            {' '}
            <h1>Are you sure you want to delete this {type}? This action cannot be undone.</h1>
            <div className="source-partner__delete-confirm-modal__button-container">
              <Button
                className="source-partner__delete-confirm-modal__confirm-button"
                onClick={() => {
                  onConfirm()
                }}
              >
                Permanently Delete
              </Button>{' '}
              <Button onClick={() => setIsOpen(false)}>Cancel Operation</Button>{' '}
            </div>
          </div>
        </section>
      </Dialog>
    </div>
  )
}
const renderDeleteButton = (onConfirm, type) => <DeleteButton onConfirm={onConfirm} type={type} />

const SourcePartnerPage = (props) => {
  const { pdpEnvironment } = usePDPEnvironment()
  const { env } = props
  const { user } = useUser()
  const { id } = useParams()
  const [partnerData, setPartnerData] = useState(null)
  const [s3AccessKeys, setS3AccessKeys] = useState([])
  const [s3AccessKeyData, setS3AccessKeyData] = useState(null)

  const [enumMappingId, setEnumMappingId] = useState([])
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false)

  useEffect(() => {
    if (id) {
      getMappingIds().then((data) => setEnumMappingId(data.items))
      fetchSourcePartner()
      fetchS3AccessKeys()
    }
  }, [id])

  const fetchSourcePartner = async () => {
    try {
      const response = await getSourcePartnersById(pdpEnvironment.value, id)
      if (response) {
        setPartnerData(response)
      }
    } catch (error) {
      toast.error('Error while fetching source partner: ' + error.message)
    }
  }

  const fetchS3AccessKeys = async () => {
    try {
      const response = await getS3AccessKeys(pdpEnvironment.value, id)
      if (response) {
        setS3AccessKeys(response?.filter((key) => !key.deleted) || [])
      }
    } catch (error) {
      toast.error('Error while fetching source partner: ' + error.message)
    }
  }

  const toggleNewApiKeyModal = () => {
    setApiKeyModalOpen((prev) => !prev)
  }

  const closeNewApiKeyModalClose = () => {
    setApiKeyModalOpen(false)
    fetchSourcePartner()
  }

  const closeS3AccessKeyDownloadModal = () => {
    setS3AccessKeyData(null)
    fetchS3AccessKeys()
  }

  const handleUpdate = async () => {
    try {
      await updateSourcePartner(pdpEnvironment.value, id, {
        email: partnerData?.email || '',
        mapping_ids: partnerData?.mappingIds || [],
      })
      toast.success('Source partner updated!')
    } catch (error) {
      toast.error('Error while updating source partner: ' + error.message)
    }
  }

  const handleCreateS3AccessKey = async () => {
    try {
      if (s3AccessKeys?.length >= 2) {
        toast.error(
          'Maximum of 2 S3 Access Keys allowed per source partner. please delete one to create a new key'
        )
        return
      }
      const data = await createS3AccessKeys(pdpEnvironment.value, id)
      toast.success('S3 Access Key created!.Please download the key now.')
      setS3AccessKeyData(data)
    } catch (error) {
      toast.error('Error while creating S3 Access Key: ')
    }
  }

  const handleDeleteS3AccessKey = async (key) => {
    try {
      await deleteS3AccessKeys(pdpEnvironment.value, id, key)
      fetchS3AccessKeys()
      toast.success('S3 Access Key deleted!.')
    } catch (error) {
      toast.error('Error while deleting S3 Access Key: ')
    }
  }

  const handleDeleteApiKey = async (key) => {
    try {
      await deleteApiKeys(pdpEnvironment.value, id, key)
      fetchSourcePartner()
      toast.success('Api Key deleted!.')
    } catch (error) {
      toast.error('Error while deleting S3 Access Key: ')
    }
  }

  const updateValues = (field, value) => {
    setPartnerData({ ...partnerData, [field]: value })
  }

  const onClose = () => {
    window._history.push(`/source-partners`)
  }

  return (
    <div id="source-partner-page" className="source-partner">
      <div>
        <Header
          title={
            <div className="predactiv-admin__header">
              <span>Source Partner</span>
              <span>:</span>
              <span>{pdpEnvironment.label}</span>
            </div>
          }
        />
        <div className="source-partner__header">
          <div>
            <button className="source-partner__back-button" onClick={onClose}>
              <Icon icon="angleleft" type="outline" color="#3d3d3d" height="28px" width="28px" />
            </button>
            <h2>Edit Source Partner</h2>
          </div>
          <div>
            <Button onClick={onClose} theme={BUTTON_THEME.SECONDARY}>
              Cancel
            </Button>
            <Button onClick={handleUpdate}>Save</Button>
          </div>
        </div>

        <div>
          <div className="source-partner__section-title">
            <h2>General</h2>
          </div>
          <hr />
          <div className="source-partner__block">
            <div className="source-partner__group">
              <Input
                name="name"
                label="Name"
                type="text"
                value={partnerData?.name}
                disabled={true}
              />
              <Input
                name="slug"
                label="Slug"
                type="text"
                value={partnerData?.slug}
                disabled={true}
              />
              <div className="source-partner__group__id-container">
                <Input
                  name="_id"
                  label="Partner id"
                  type="text"
                  value={partnerData?._id}
                  disabled={true}
                />
                <div
                  style={{ cursor: 'pointer' }}
                  data-tip
                  data-for={`copy-id`}
                  onClick={() => {
                    copyToClipBoard(partnerData?._id)
                    toast.success('Copied to clipboard successfully')
                  }}
                >
                  <Icon color="#02892dff" height="35px" icon="file" type="fill" width="35px" />
                </div>
                <ReactTooltip id={`copy-id`} place="top">
                  Copy to clipboard{' '}
                </ReactTooltip>
              </div>

              <Input
                name="email"
                label="Partner Email  ( Optional )"
                type="text"
                value={partnerData?.email}
                onChange={(val) => updateValues('email', val)}
              />
            </div>
            <div className="source-partner__group">
              <Select
                label="Mapping Ids"
                value={partnerData?.mappingIds || []}
                isMulti
                options={enumMappingId}
                onChange={(option, name, val) => updateValues('mappingIds', val)}
                useReactSelect
              />
            </div>
          </div>
          <div className="source-partner__keys-container">
            <div className="source-partner__section-title">
              <h2>API Keys</h2>
              <Button onClick={toggleNewApiKeyModal}>Create New Api Key</Button>
            </div>
            <hr />
            <If condition={partnerData?.apiKeys && partnerData.apiKeys?.length > 0}>
              <Table
                columns={[
                  {
                    name: 'Name',
                    key: 'name',
                  },
                  {
                    name: 'Api Key',
                    key: 'apiKey',
                    render: () => 'XXXXXXX',
                  },
                  {
                    name: 'Created At',
                    key: 'createdAt',
                  },
                  {
                    name: 'Created By',
                    key: 'createdBy',
                  },
                  {
                    name: '',
                    styles: {
                      width: '10%',
                      textAlign: 'right',
                    },
                    render: (item) =>
                      renderDeleteButton(() => handleDeleteApiKey(item.apiKey), 'Api key'),
                  },
                ]}
                data={partnerData.apiKeys || []}
              />
              <Else />
              <div className="flex justify-center items-center py-6 text-gray-500 text-2xl font-bold">
                No Api Keys yet.
              </div>
            </If>
          </div>
          <div>
            <div className="source-partner__section-title">
              <h2>S3 Access Keys</h2>
              <Button onClick={handleCreateS3AccessKey}>Create S3 Access Key</Button>
            </div>
            <hr />
            <If condition={s3AccessKeys && s3AccessKeys.length > 0}>
              <Table
                columns={[
                  {
                    name: 'Public Access Key',
                    key: 'publicAccessKey',
                  },
                  {
                    name: 'Created At',
                    key: 'createdAt',
                  },
                  {
                    name: 'Created By',
                    key: 'createdBy.email',
                  },
                  {
                    name: '',
                    styles: {
                      width: '10%',
                      textAlign: 'right',
                    },
                    render: (item) =>
                      renderDeleteButton(
                        () => handleDeleteS3AccessKey(item.publicAccessKey),
                        'S3 Access Key'
                      ),
                  },
                ]}
                data={s3AccessKeys || []}
              />
              <Else />
              <div className="flex justify-center items-center py-6 text-gray-500 text-2xl font-bold">
                No S3 Access Keys yet.
              </div>
            </If>
          </div>
        </div>
      </div>
      {partnerData && (
        <Dialog open={apiKeyModalOpen} close={toggleNewApiKeyModal}>
          <NewApiKeyModal
            open={apiKeyModalOpen}
            env={env}
            id={id}
            partnerName={partnerData?.name}
            createdBy={user?.email}
            title="Enter a name for the new Api Key"
            close={closeNewApiKeyModalClose}
          />
        </Dialog>
      )}
      {s3AccessKeyData && partnerData && (
        <Dialog open={s3AccessKeyData}>
          <NewS3AccessKeyModal
            data={s3AccessKeyData}
            partnerName={partnerData?.name}
            close={closeS3AccessKeyDownloadModal}
          />
        </Dialog>
      )}
    </div>
  )
}

export default SourcePartnerPage
